import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, formatCurrency } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MoreHorizontal, Pencil, Trash2, CalendarIcon, Check, Clock, CheckCircle2, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, colorClass, bgClass }) => (
  <div className={`flex items-center gap-4 p-4 rounded-xl ${bgClass}`}>
    <div className={`p-2 rounded-lg ${bgClass}`}>
      <Icon className={`w-5 h-5 ${colorClass}`} />
    </div>
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{title}</p>
      <p className={`font-bold text-xl ${colorClass}`}>{value}</p>
    </div>
  </div>
);

// Add/Edit Bill Dialog (for expenses)
const BillDialog = ({ bill, onClose }) => {
  const { selectedCycle, addBill, updateBill, categories } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(bill?.name || "");
  const [categoryId, setCategoryId] = useState(bill?.categoryId || "");
  const [amount, setAmount] = useState(bill?.amount?.toString() || "");
  const [dueDate, setDueDate] = useState(bill?.dueDateISO ? new Date(bill.dueDateISO) : null);
  const [loading, setLoading] = useState(false);

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const isEdit = !!bill;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const data = {
        name,
        categoryId: categoryId || null,
        amount: parseFloat(amount),
        dueDateISO: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
        type: 'expense'
      };
      
      if (isEdit) {
        updateBill(bill.id, data);
        toast.success("Bill updated!");
      } else {
        addBill({ ...data, cycleKey: selectedCycle, isPaid: false });
        toast.success("Bill added!");
      }
      setOpen(false);
      if (onClose) onClose();
    } catch (error) {
      toast.error(isEdit ? "Failed to update bill" : "Failed to add bill");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil className="w-4 h-4 mr-2" />Edit
          </DropdownMenuItem>
        ) : (
          <Button className="rounded-full bg-rose-600 hover:bg-rose-700" data-testid="add-bill-btn">
            <Plus className="w-4 h-4 mr-2" />Add Expense
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isEdit ? "Edit Bill" : "Add Expense Bill"}</DialogTitle>
          <DialogDescription>Enter the expense details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Bill Name</Label>
            <Input placeholder="e.g., Rent, Internet" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="space-y-2">
            <Label>Category (optional)</Label>
            <Select value={categoryId || "none"} onValueChange={(val) => setCategoryId(val === "none" ? "" : val)}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {expenseCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Amount (£)</Label>
            <Input type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} min="0" step="0.01" required />
          </div>

          <div className="space-y-2">
            <Label>Due Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Bill" : "Add Bill"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add/Edit Income Dialog
const IncomeDialog = ({ income, onClose }) => {
  const { selectedCycle, addBill, updateBill, categories } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(income?.name || "");
  const [categoryId, setCategoryId] = useState(income?.categoryId || "");
  const [amount, setAmount] = useState(income?.amount?.toString() || "");
  const [expectedDate, setExpectedDate] = useState(income?.dueDateISO ? new Date(income.dueDateISO) : null);
  const [loading, setLoading] = useState(false);

  const incomeCategories = categories.filter(c => c.type === 'income');
  const isEdit = !!income;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const data = {
        name,
        categoryId: categoryId || null,
        amount: parseFloat(amount),
        dueDateISO: expectedDate ? format(expectedDate, "yyyy-MM-dd") : null,
        type: 'income'
      };
      
      if (isEdit) {
        updateBill(income.id, data);
        toast.success("Income updated!");
      } else {
        addBill({ ...data, cycleKey: selectedCycle, isPaid: false });
        toast.success("Expected income added!");
      }
      setOpen(false);
      if (onClose) onClose();
    } catch (error) {
      toast.error(isEdit ? "Failed to update income" : "Failed to add income");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil className="w-4 h-4 mr-2" />Edit
          </DropdownMenuItem>
        ) : (
          <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="add-income-btn">
            <Plus className="w-4 h-4 mr-2" />Add Income
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isEdit ? "Edit Income" : "Add Expected Income"}</DialogTitle>
          <DialogDescription>Enter the expected income details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Income Name</Label>
            <Input placeholder="e.g., Salary, Freelance" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="space-y-2">
            <Label>Category (optional)</Label>
            <Select value={categoryId || "none"} onValueChange={(val) => setCategoryId(val === "none" ? "" : val)}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {incomeCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Amount (£)</Label>
            <Input type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} min="0" step="0.01" required />
          </div>

          <div className="space-y-2">
            <Label>Expected Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {expectedDate ? format(expectedDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={expectedDate} onSelect={setExpectedDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <Button type="submit" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Income" : "Add Income"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Dialog
const DeleteItemDialog = ({ item, type }) => {
  const [open, setOpen] = useState(false);
  const { deleteBill } = useApp();

  const handleDelete = () => {
    deleteBill(item.id);
    toast.success(`${type === 'income' ? 'Income' : 'Bill'} deleted!`);
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setOpen(true); }} className="text-rose-600">
        <Trash2 className="w-4 h-4 mr-2" />Delete
      </DropdownMenuItem>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {type === 'income' ? 'Income' : 'Bill'}</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to delete "{item.name}"?</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Bill/Expense Row
const BillRow = ({ bill }) => {
  const { toggleBillPaid } = useApp();
  const navigate = useNavigate();

  const handleViewTransaction = () => {
    if (bill.linkedTransactionId) {
      navigate('/transactions');
    }
  };

  return (
    <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
      bill.isPaid ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-stone-200 hover:border-stone-300"
    }`}>
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${bill.isPaid ? "bg-emerald-100" : "bg-rose-100"}`}>
          {bill.isPaid ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-rose-600" />}
        </div>
        <div>
          <p className={`font-semibold ${bill.isPaid ? "text-stone-500 line-through" : "text-stone-900"}`}>{bill.name}</p>
          {bill.dueDateISO && (
            <p className="text-xs text-stone-400">
              Due: {new Date(bill.dueDateISO).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </p>
          )}
          {bill.isPaid && bill.paidDateISO && (
            <p className="text-xs text-emerald-600">
              Paid: {new Date(bill.paidDateISO).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              {bill.linkedTransactionId && (
                <button onClick={handleViewTransaction} className="ml-2 underline hover:text-emerald-700">
                  View in Transactions →
                </button>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <p className={`font-mono font-bold text-lg ${bill.isPaid ? "text-stone-400" : "text-rose-600"}`}>
          -{formatCurrency(bill.amount)}
        </p>
        
        <div className="flex items-center gap-2">
          <Switch checked={bill.isPaid} onCheckedChange={() => toggleBillPaid(bill.id)} />
          <span className={`text-xs font-medium ${bill.isPaid ? "text-emerald-600" : "text-rose-600"}`}>
            {bill.isPaid ? "Paid" : "Unpaid"}
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <BillDialog bill={bill} />
            <DeleteItemDialog item={bill} type="expense" />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Income Row
const IncomeRow = ({ income }) => {
  const { toggleBillPaid } = useApp();
  const navigate = useNavigate();

  const handleViewTransaction = () => {
    if (income.linkedTransactionId) {
      navigate('/transactions');
    }
  };

  return (
    <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
      income.isPaid ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-stone-200 hover:border-stone-300"
    }`}>
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${income.isPaid ? "bg-emerald-100" : "bg-amber-100"}`}>
          {income.isPaid ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <TrendingUp className="w-5 h-5 text-amber-600" />}
        </div>
        <div>
          <p className={`font-semibold ${income.isPaid ? "text-stone-500 line-through" : "text-stone-900"}`}>{income.name}</p>
          {income.dueDateISO && (
            <p className="text-xs text-stone-400">
              Expected: {new Date(income.dueDateISO).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </p>
          )}
          {income.isPaid && income.paidDateISO && (
            <p className="text-xs text-emerald-600">
              Received: {new Date(income.paidDateISO).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              {income.linkedTransactionId && (
                <button onClick={handleViewTransaction} className="ml-2 underline hover:text-emerald-700">
                  View in Transactions →
                </button>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <p className={`font-mono font-bold text-lg ${income.isPaid ? "text-stone-400" : "text-emerald-600"}`}>
          +{formatCurrency(income.amount)}
        </p>
        
        <div className="flex items-center gap-2">
          <Switch checked={income.isPaid} onCheckedChange={() => toggleBillPaid(income.id)} />
          <span className={`text-xs font-medium ${income.isPaid ? "text-emerald-600" : "text-amber-600"}`}>
            {income.isPaid ? "Received" : "Upcoming"}
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <IncomeDialog income={income} />
            <DeleteItemDialog item={income} type="income" />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default function Bills() {
  const { bills, currentSummary, selectedCycle, getCycleDateRange } = useApp();

  const cycleBills = bills.filter(b => b.cycleKey === selectedCycle);
  
  // Separate income and expenses
  const incomeItems = cycleBills.filter(b => b.type === 'income');
  const expenseItems = cycleBills.filter(b => b.type !== 'income'); // default to expense if no type
  
  // Sort items
  const sortedExpenses = [...expenseItems].sort((a, b) => {
    if (a.isPaid !== b.isPaid) return a.isPaid ? 1 : -1;
    if (a.dueDateISO && b.dueDateISO) return new Date(a.dueDateISO) - new Date(b.dueDateISO);
    return 0;
  });
  
  const sortedIncome = [...incomeItems].sort((a, b) => {
    if (a.isPaid !== b.isPaid) return a.isPaid ? 1 : -1;
    if (a.dueDateISO && b.dueDateISO) return new Date(a.dueDateISO) - new Date(b.dueDateISO);
    return 0;
  });

  // Calculate totals
  const totalExpectedIncome = incomeItems.reduce((sum, i) => sum + i.amount, 0);
  const totalReceivedIncome = incomeItems.filter(i => i.isPaid).reduce((sum, i) => sum + i.amount, 0);
  const totalExpenses = expenseItems.reduce((sum, i) => sum + i.amount, 0);
  const totalPaidExpenses = expenseItems.filter(i => i.isPaid).reduce((sum, i) => sum + i.amount, 0);

  const currentCycleRange = selectedCycle ? getCycleDateRange(selectedCycle) : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bills-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl text-stone-900">Bills & Income</h2>
          <p className="text-stone-500 mt-1">{currentCycleRange?.label}</p>
        </div>
        <div className="flex gap-2">
          <IncomeDialog />
          <BillDialog />
        </div>
      </div>

      {/* Summary Cards */}
      <Card className="p-4 bg-white rounded-2xl border border-stone-200/60">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard title="Expected Income" value={formatCurrency(totalExpectedIncome)} icon={TrendingUp} colorClass="text-emerald-700" bgClass="bg-emerald-50" />
          <SummaryCard title="Received" value={formatCurrency(totalReceivedIncome)} icon={Check} colorClass="text-emerald-700" bgClass="bg-emerald-50" />
          <SummaryCard title="Total Bills" value={formatCurrency(totalExpenses)} icon={TrendingDown} colorClass="text-rose-700" bgClass="bg-rose-50" />
          <SummaryCard title="Paid" value={formatCurrency(totalPaidExpenses)} icon={Check} colorClass="text-rose-700" bgClass="bg-rose-50" />
        </div>
      </Card>

      {/* Income Section */}
      <Card className="p-5 bg-white rounded-2xl border border-stone-200/60">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Expected Income
          </h3>
          <p className="text-sm text-stone-500">
            {incomeItems.filter(i => i.isPaid).length}/{incomeItems.length} received
          </p>
        </div>
        {sortedIncome.length > 0 ? (
          <div className="space-y-3">
            {sortedIncome.map((income) => <IncomeRow key={income.id} income={income} />)}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-stone-400">
            <TrendingUp className="w-8 h-8 mb-2 opacity-50" />
            <p className="font-medium">No expected income</p>
            <p className="text-sm">Add your salary or other income sources</p>
          </div>
        )}
      </Card>

      {/* Bills/Expenses Section */}
      <Card className="p-5 bg-white rounded-2xl border border-stone-200/60">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-rose-600" />
            Bills & Expenses
          </h3>
          <p className="text-sm text-stone-500">
            {expenseItems.filter(i => i.isPaid).length}/{expenseItems.length} paid
          </p>
        </div>
        {sortedExpenses.length > 0 ? (
          <div className="space-y-3">
            {sortedExpenses.map((bill) => <BillRow key={bill.id} bill={bill} />)}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-stone-400">
            <TrendingDown className="w-8 h-8 mb-2 opacity-50" />
            <p className="font-medium">No bills planned</p>
            <p className="text-sm">Add your monthly bills to track payments</p>
          </div>
        )}
      </Card>
    </div>
  );
}
